/* eslint-disable */
// useLensDevFiles — LTD image gallery + memo, persisted in lens_developments.custom_fields (jsonb).
// Phase B of the Lens-Dev carousel brief. Unlike Devs' useFilesV2 (direct developments.data update),
// lens_developments is RLS-gated: every write goes through the SECURITY DEFINER RPC lens_custom_save
// (has_lens_admin = editor+), which does a shallow top-level `|| ` merge — so we send only the changed
// keys ({images} / {images,primaryImageIndex} / {memo}) and materials / PCPL custom values survive.
//
// Image object shape (superset of Devs' bare strings): { id, url, path, kind:'round'|'framed' }.
// Legacy read-normalization (§3, no backfill): custom_fields.images empty → synthesize from the
// photo_original_url / photo_framed_url columns; if the two are equal, emit a SINGLE image (dedupe,
// refinement #4) so post-promote archived LTDs don't render a duplicate round+framed pair.
// photo_original_url is kept in sync with the cover URL (§5) via ltd_update — back-compat for the
// promote coalesce(photo_framed_url, photo_original_url).
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { compressImage, uploadImageToStorage, removeBgForUrl } from '../../../../utils';
import { lensCustomSave } from '../fieldsStudio/fieldsApi';
import { LTD_KEY } from './ltdApi';

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const pathOf = (url) => { const p = String(url || '').split('/item-images/'); return p.length >= 2 ? p[1] : null; };

const normImg = (x, i) => {
  if (!x) return null;
  if (typeof x === 'string') return x ? { id: `img-${i}`, url: x, path: pathOf(x), kind: 'round' } : null;
  const url = x.url || x.src || '';
  if (!url) return null;
  return { id: x.id || `img-${i}`, url, path: x.path || pathOf(url), kind: x.kind === 'framed' ? 'framed' : 'round', bgRemoved: !!x.bgRemoved };
};

// Pure read-path (also consumed by LtdCard for its read-only carousel — no hook overhead per card).
export function readLensDevImages(dev) {
  const cf = (dev && dev.custom_fields) || {};
  const raw = Array.isArray(cf.images) ? cf.images : [];
  if (raw.length) return raw.map(normImg).filter(Boolean);
  // legacy synth from the two photo columns
  const orig = (dev && dev.photo_original_url) || '';
  const framed = (dev && dev.photo_framed_url) || '';
  const out = [];
  if (orig) out.push({ id: 'legacy-round', url: orig, path: pathOf(orig), kind: 'round' });
  if (framed && framed !== orig) out.push({ id: 'legacy-framed', url: framed, path: pathOf(framed), kind: 'framed' });
  return out;
}
// P4 · attach the FIRST image to a development that has just been created.
//
// The create modal cannot use the hook's `upload`: that one is bound to an existing dev and to the
// current images array, and at create time there is neither — the row does not exist until ltd_create
// returns. What it can share is the SHAPE, which is the part that must not drift: the object below is
// built by the same `normImg`-compatible construction the hook uses, written through the same
// `lensCustomSave` + `photo_original_url` pair, so a photo added at creation is indistinguishable
// from one added later on the card.
export async function attachFirstLensDevImage(devId, file, actions) {
  if (!devId || !file || !file.type || !file.type.startsWith('image/')) return null;
  const dataUrl = await compressImage(file);
  const url = await uploadImageToStorage(dataUrl);
  if (!url) throw new Error('upload failed');
  const obj = { id: uid(), url, path: pathOf(url), kind: 'round' };
  await lensCustomSave('lens_developments', devId, { images: [obj], primaryImageIndex: 0 });
  await actions.update(devId, { photo_original_url: url });
  return url;
}

const readMemo = (dev) => { const m = (dev && dev.custom_fields && dev.custom_fields.memo); return Array.isArray(m) ? m : []; };

export function useLensDevFiles({ dev, actions, canWrite, showToast }) {
  const qc = useQueryClient();
  const devId = dev && dev.id;
  const writingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [removingBgIdx, setRemovingBgIdx] = useState(null);
  // transient optimistic overlay — one op at a time (writingRef serializes), cleared after refetch.
  const [ov, setOv] = useState({});
  useEffect(() => { setOv({}); }, [devId]);

  const serverImages = useMemo(() => readLensDevImages(dev), [dev]);
  const serverMemo = useMemo(() => readMemo(dev), [dev]);
  const serverPrimary = useMemo(() => {
    const p = dev && dev.custom_fields && dev.custom_fields.primaryImageIndex;
    return Number.isInteger(p) ? p : 0;
  }, [dev]);

  const images = ov.images != null ? ov.images : serverImages;
  const memo = ov.memo != null ? ov.memo : serverMemo;
  const rawPrimary = ov.primaryIndex != null ? ov.primaryIndex : serverPrimary;
  const primaryIndex = images.length ? Math.min(Math.max(0, rawPrimary), images.length - 1) : 0;

  // Persist a partial custom_fields patch (|| merge server-side) + optional photo_original_url sync,
  // then refetch the list so the modal's dev prop reflects the write, then drop the overlay.
  const persist = useCallback(async (patch, coverUrl) => {
    await lensCustomSave('lens_developments', devId, patch);
    if (coverUrl !== undefined && coverUrl !== ((dev && dev.photo_original_url) || null)) {
      await actions.update(devId, { photo_original_url: coverUrl }); // §5 back-compat; also invalidates LTD_KEY
    }
    await qc.refetchQueries({ queryKey: LTD_KEY });
    setOv({});
  }, [devId, dev, actions, qc]);

  // Serialize + guard every mutation; optimistic overlay for instant UI; revert overlay on failure.
  const run = useCallback(async (optimistic, fn) => {
    if (!canWrite || !devId || writingRef.current) return;
    writingRef.current = true; setBusy(true); setOv(optimistic);
    try { await fn(); }
    catch (e) {
      setOv({});
      // the failing call's own sentence — for a background removal, bg-cutout's (SYS-10b)
      if (showToast) showToast('Image update failed: ' + (e && (e.message || e)), true);
    } finally { writingRef.current = false; setBusy(false); }
  }, [canWrite, devId, showToast]);

  const coverOf = (arr, idx) => (arr[idx] && arr[idx].url) || null;

  const upload = useCallback(async (file, kind) => {
    if (!file || !file.type || !file.type.startsWith('image/')) return;
    const dataUrl = await compressImage(file);
    const url = await uploadImageToStorage(dataUrl);
    if (!url) throw new Error('upload failed');
    const obj = { id: uid(), url, path: pathOf(url), kind: kind === 'framed' ? 'framed' : 'round' };
    const next = [...images, obj];
    const primary = images.length ? primaryIndex : 0; // first image becomes the cover
    return run({ images: next, primaryIndex: primary }, () =>
      persist({ images: next, primaryImageIndex: primary }, coverOf(next, primary)));
  }, [images, primaryIndex, run, persist]);

  const setPrimary = useCallback((idx) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= images.length || idx === primaryIndex) return;
    return run({ images, primaryIndex: idx }, () =>
      persist({ images, primaryImageIndex: idx }, coverOf(images, idx)));
  }, [images, primaryIndex, run, persist]);

  const remove = useCallback((idx) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= images.length) return;
    const next = images.filter((_, i) => i !== idx);
    let primary = primaryIndex;
    if (idx === primary) primary = 0;
    else if (idx < primary) primary = Math.max(0, primary - 1);
    if (primary >= next.length) primary = Math.max(0, next.length - 1);
    return run({ images: next, primaryIndex: primary }, () =>
      persist({ images: next, primaryImageIndex: primary }, coverOf(next, primary)));
  }, [images, primaryIndex, run, persist]);

  const reorder = useCallback((from, to) => {
    if (!Number.isInteger(from) || !Number.isInteger(to) || from === to) return;
    if (from < 0 || to < 0 || from >= images.length || to >= images.length) return;
    const keepId = images[primaryIndex] && images[primaryIndex].id;
    const next = images.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const primary = Math.max(0, next.findIndex((o) => o.id === keepId));
    return run({ images: next, primaryIndex: primary }, () =>
      persist({ images: next, primaryImageIndex: primary }, coverOf(next, primary)));
  }, [images, primaryIndex, run, persist]);

  const removeBg = useCallback((idx) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= images.length) return;
    const target = images[idx];
    if (!target || !target.url) return;
    setRemovingBgIdx(idx);
    const finish = () => setRemovingBgIdx(null);
    return run({ images, primaryIndex }, async () => {
      try {
        const newUrl = await removeBgForUrl(target.url, { deleteOriginal: true });
        if (!newUrl) throw new Error('background removal returned empty');
        const next = images.map((o, i) => (i === idx ? { ...o, url: newUrl, path: pathOf(newUrl), bgRemoved: true } : o));
        setOv({ images: next, primaryIndex });
        await persist({ images: next, primaryImageIndex: primaryIndex }, coverOf(next, primaryIndex));
      } finally { finish(); }
    }).finally(finish);
  }, [images, primaryIndex, run, persist]);

  // ── memo ──────────────────────────────────────────────────────────────────
  const addNote = useCallback((text) => {
    const t = (text || '').trim(); if (!t) return;
    const next = [{ id: uid(), text: t, created_at: new Date().toISOString(), struck: false }, ...memo];
    return run({ memo: next }, () => persist({ memo: next }));
  }, [memo, run, persist]);

  const toggleStrike = useCallback((id) => {
    const next = memo.map((n) => (n.id === id ? { ...n, struck: !n.struck } : n));
    return run({ memo: next }, () => persist({ memo: next }));
  }, [memo, run, persist]);

  const deleteNote = useCallback((id) => {
    const next = memo.filter((n) => n.id !== id);
    return run({ memo: next }, () => persist({ memo: next }));
  }, [memo, run, persist]);

  return { images, primaryIndex, memo, busy, removingBgIdx, canWrite: !!canWrite,
    upload, setPrimary, remove, reorder, removeBg, addNote, toggleStrike, deleteNote };
}
